import React from 'react';
import { InfoIcon, Copy, Loader2, Download, FileCheck } from 'lucide-react';
import { useShippingAddresses } from '../hooks/useShippingAddresses';
import { useAuth } from '../auth/hooks/useAuth';
import { showErrorToast, showSuccessToast } from '../utils/toast';
import { useTrackEvent } from '../utils/track';

interface FormattedAddressDisplay {
  name: string;
  addressNameFormat: string;
  addressLine1Format: string;
  addressLine2Format?: string;
  addressCityFormat: string;
  addressStateFormat: string;
  addressZipFormat: string;
  addressCountryFormat: string;
  hasTaxFreeCertificate?: boolean;
  taxFreeCertificateUrl?: string | null;
  taxFreeCertificateName?: string | null;
}

interface MailboxSettings {
  prefix?: string;
}

interface MailboxUserData {
  mailboxNumber?: string;
}
const Mailbox = () => {
  const { data, isLoading, error } = useShippingAddresses();
  const { user } = useAuth();
  const track = useTrackEvent();

  // Warehouse couriers view this screen to relay the address format to their own
  // customers, so the helper copy differs from the customer-facing wording.
  const isCourier = user?.isCourier === true;

  // Combine address for easy copying
  const formatAddress = (address: FormattedAddressDisplay) => {
    return `${address.addressNameFormat}
${address.addressLine1Format}
${address.addressLine2Format || ''}
${address.addressCityFormat}
${address.addressStateFormat}
${address.addressZipFormat}
${address.addressCountryFormat}`;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      track('address_copied');
      showSuccessToast({
        title: 'Address copied to clipboard!',
        message: 'You can now paste this address into your shipping label.',
      });
    }).catch(err => {
      console.error('Failed to copy: ', err);
      showErrorToast({
        title: 'Failed to copy address',
        message: 'Please try again.',
      });
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 m-6">
        <p className="text-red-800 dark:text-red-300">
          Error loading shipping addresses: {error.message}
        </p>
      </div>
    );
  }

  if (!data || !data.addresses || data.addresses.length === 0) {
    return (
      <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 m-6">
        <p className="text-yellow-800 dark:text-yellow-300">
          No shipping addresses available.
        </p>
      </div>
    );
  }
  return <div className="bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="max-w-4xl mx-auto p-6 pb-24 md:pb-6">
        {/* Info Alert */}
        <div className="bg-primary/5 dark:bg-primary/10 border border-primary/20 rounded-lg p-4 mb-6">
          <div className="flex">
            <InfoIcon className="text-primary h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
            <p className="text-primary">
              {isCourier
                ? "This is the shipping address format to share with your customers. Each customer's own mailbox number replaces the sample shown below — the rest of the address stays the same."
                : 'These are your personal forwarding addresses, please send your packages to the appropriate address based on shipping method'}
            </p>
          </div>
        </div>
        {/* Loop through all mailboxes */}
        <div className="space-y-6">
          {(data.addresses as FormattedAddressDisplay[]).map((address, index) => (
            <div key={index} className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center">
                  <div className="bg-primary/10 dark:bg-primary/20 rounded-full p-2 mr-3">
                    <svg 
                      className="w-6 h-6 text-primary" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    >
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-bold text-lg dark:text-white flex items-center gap-2">
                      {address.name}
                      {address.taxFreeCertificateUrl && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">
                          <FileCheck className="h-3 w-3" aria-hidden="true" />
                          Tax-free
                        </span>
                      )}
                    </h3>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {(data.settings as MailboxSettings)?.prefix && (data.userData as MailboxUserData)?.mailboxNumber 
                        ? `${(data.settings as MailboxSettings).prefix}-${(data.userData as MailboxUserData).mailboxNumber}`
                        : 'Mailbox Address'
                      }
                    </div>
                  </div>
                </div>
                <button onClick={() => copyToClipboard(formatAddress(address))} className="text-primary dark:text-primary hover:text-blue-700 dark:hover:text-blue-300 p-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/50" title="Copy address">
                  <Copy className="h-4 w-4" />
                </button>
              </div>
              <div className="space-y-2">
                <div className="flex">
                  <span className="font-medium w-28 text-gray-600 dark:text-gray-400">
                    Name:
                  </span>
                  <span className="dark:text-white">{address.addressNameFormat}</span>
                </div>
                <div className="flex">
                  <span className="font-medium w-28 text-gray-600 dark:text-gray-400">
                    Street 1:
                  </span>
                  <span className="dark:text-white">{address.addressLine1Format}</span>
                </div>
                {address.addressLine2Format && (
                  <div className="flex">
                    <span className="font-medium w-28 text-gray-600 dark:text-gray-400">
                      Street 2:
                    </span>
                    <span className="dark:text-white">{address.addressLine2Format}</span>
                  </div>
                )}
                <div className="flex">
                  <span className="font-medium w-28 text-gray-600 dark:text-gray-400">
                    City:
                  </span>
                  <span className="dark:text-white">{address.addressCityFormat}</span>
                </div>
                <div className="flex">
                  <span className="font-medium w-28 text-gray-600 dark:text-gray-400">
                    State:
                  </span>
                  <span className="dark:text-white">{address.addressStateFormat}</span>
                </div>
                <div className="flex">
                  <span className="font-medium w-28 text-gray-600 dark:text-gray-400">
                    Zip/Postal:
                  </span>
                  <span className="dark:text-white">{address.addressZipFormat}</span>
                </div>
                <div className="flex">
                  <span className="font-medium w-28 text-gray-600 dark:text-gray-400">
                    Country:
                  </span>
                  <span className="dark:text-white">{address.addressCountryFormat}</span>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2">
                <button onClick={() => copyToClipboard(formatAddress(address))} className="text-sm text-primary dark:text-primary hover:text-blue-700 dark:hover:text-blue-300 flex items-center">
                  <Copy className="h-4 w-4 mr-1" />
                  Copy Address
                </button>
                {address.taxFreeCertificateUrl && (
                  <a
                    href={address.taxFreeCertificateUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={address.taxFreeCertificateName || undefined}
                    className="text-sm text-primary dark:text-primary hover:text-blue-700 dark:hover:text-blue-300 flex items-center"
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Download Tax-Exempt Certificate
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
       
      </div>
    </div>;
};
export default Mailbox;